import { FUSO_BRASILIA } from '@/shared/formatters/date';
import { isRecord } from '@/shared/utils/apiResponse';
import { formatTelefoneComCodigoPais } from '@/shared/utils/contato';
import { ehUrlNavegavelSegura } from '@/shared/utils/urlSegura';

import {
  FONTES_DO_CERTIFICADO,
  type CertificadoNegociacaoResumoDto,
  type ContatoDoCertificado,
  type ConversaDoCertificado,
  type ErroDoCertificadoNegociacao,
  type FatiaDaMensagemDoCertificado,
  type FatiaDoTextoCertificado,
  type FonteDoCertificado,
  type ItemDeMensagemDoCertificado,
  type MensagemDoCertificado,
  type OpcoesDaRotaDoCertificado,
  type UrlDeValidacaoExibivel,
} from '../types/certificadoNegociacaoTypes';

/**
 * O 409 do core quando há mensagem cujo pertencimento ao caso é ambíguo (review do PR #2356,
 * COR-05): nenhuma emissão é criada até o vínculo ser conferido, e a tela diz isso, sem contagem.
 */
const CODIGO_VINCULO_A_RECONCILIAR = 'CERTIFICADO_VINCULO_A_RECONCILIAR';

export const AVISO_DE_VINCULO_A_RECONCILIAR =
  'Há mensagens desta negociação cujo vínculo com o caso precisa ser conferido antes da emissão do certificado.';

export const ehVinculoAReconciliar = (erro: ErroDoCertificadoNegociacao | null): boolean =>
  erro?.code === CODIGO_VINCULO_A_RECONCILIAR;

/**
 * Junta os itens da seção `mensagens` nos blocos de conversa, na ordem recebida. Um bloco que
 * atravessa duas páginas continua um bloco só: o que agrupa é o `bloco.indice` do item, e não a
 * página em que ele chegou.
 */
export function agruparMensagensEmConversas(
  itens: ItemDeMensagemDoCertificado[],
): ConversaDoCertificado[] {
  const conversas: ConversaDoCertificado[] = [];
  for (const { bloco, mensagem } of itens) {
    const atual = conversas.at(-1);
    if (atual?.indice === bloco.indice) {
      atual.mensagens.push(mensagem);
      continue;
    }
    conversas.push({ ...bloco, mensagens: [mensagem] });
  }
  return conversas;
}

/**
 * O "Baixar PDF" da aba leva à página com este parâmetro, e a página o lê para baixar a emissão
 * que carregar. Os dois lados usam a mesma constante para não divergirem.
 */
export const PARAMETRO_BAIXAR_AO_ABRIR = { nome: 'baixar', valor: '1' } as const;

export function rotaDoCertificado(
  casoId: string,
  { baixar = false }: OpcoesDaRotaDoCertificado = {},
): string {
  const rota = `/negociacoes/${encodeURIComponent(casoId)}/certificado-acessos`;
  if (!baixar) return rota;
  return `${rota}?${PARAMETRO_BAIXAR_AO_ABRIR.nome}=${PARAMETRO_BAIXAR_AO_ABRIR.valor}`;
}

export const dataCertificadoOuNulo = (valor: string | null | undefined): string | null => {
  if (!valor || !Number.isFinite(Date.parse(valor))) return null;
  return new Intl.DateTimeFormat('pt-BR', {
    dateStyle: 'short',
    timeStyle: 'medium',
    timeZone: FUSO_BRASILIA,
  }).format(new Date(valor));
};

export const dataCertificado = (valor: string | null | undefined): string =>
  dataCertificadoOuNulo(valor) ?? 'Não informado';

export function contatosDaContraparte(contatos: ContatoDoCertificado[]): string[] {
  if (!contatos.length) return ['WhatsApp não informado'];
  return [...new Set(contatos.map((contato) => contato.numero))].map(
    (numero) => `WhatsApp · ${formatTelefoneComCodigoPais(numero)}`,
  );
}

/**
 * A URL de validação vira `href` num documento lido por terceiros, e o React não neutraliza
 * `javascript:`. Só URL absoluta http(s): o endereço é impresso e precisa ser digitável. Fora
 * disso, a seção de autenticidade mostra que a conferência não está disponível.
 *
 * A exibição é a do PDF do core (`urlValidacaoExibicao`): só sem o esquema, com o `?code=`. Tela e
 * PDF mostram o mesmo endereço, e quem digita o impresso já chega com o código preenchido.
 */
export function urlDeValidacaoExibivel(url: string): UrlDeValidacaoExibivel | null {
  if (!ehUrlNavegavelSegura(url) || !/^https?:\/\//i.test(url)) return null;
  return { href: url, exibicao: url.replace(/^https?:\/\//i, '') };
}

export function nomeDoArquivoDoCertificado({
  caso,
  emissao,
}: CertificadoNegociacaoResumoDto): string {
  const numero = (caso.numeroNegociacao ?? 'sem-numero').replace(/[^\w-]+/g, '-').toLowerCase();
  return `certificado-negociacao-${numero}-v${emissao.versao}.pdf`;
}

/** `details.fonte` do 503 `CERTIFICADO_FONTE_INDISPONIVEL`; qualquer outra forma é nula. */
export function fonteDoErroDoCertificado(detalhes: unknown): FonteDoCertificado | null {
  if (!isRecord(detalhes)) return null;
  const { fonte } = detalhes;
  return FONTES_DO_CERTIFICADO.find((valor) => valor === fonte) ?? null;
}

/** O texto de uma mensagem no balão da conversa (`blocosConversasCertificado`). */
export const FATIA_DO_BALAO: FatiaDoTextoCertificado = {
  maxCaracteres: 1500,
  maxLinhas: 26,
  maxColunas: 56,
};

/** O valor de uma célula da tabela de dados: identificação, campos personalizados e contatos. */
export const FATIA_DA_CELULA: FatiaDoTextoCertificado = {
  maxCaracteres: 900,
  maxLinhas: 12,
  maxColunas: 55,
};

/** A descrição de um marco, impressa no card da linha do tempo (`MarcoCard`). */
export const FATIA_DA_DESCRICAO_DO_MARCO: FatiaDoTextoCertificado = {
  ...FATIA_DA_CELULA,
  maxCaracteres: 600,
};

/**
 * Quantas colunas o caractere ocupa, para estimar a quebra de linha sem medir o texto no DOM. Tab
 * vale 8. Maiúscula, `m`, `w`, símbolos e qualquer não-ASCII (acento, emoji) valem 2, por serem
 * glifos largos na fonte do documento: contá-los como 1 deixaria a fatia passar da altura da página.
 */
function larguraDoCaractere(caractere: string): number {
  if (caractere === '\t') return 8;
  return /[^\u0000-\u007f]|[A-Zmw@#%&]/u.test(caractere) ? 2 : 1;
}

/**
 * Divide o texto em fatias que cabem num bloco do documento paginado, sem partir um code point
 * (emoji inteiro). `maxCaracteres` conta unidades UTF-16 (`length`); linhas e colunas são estimadas.
 */
export function dividirTextoCertificado(
  texto: string,
  { maxCaracteres, maxLinhas, maxColunas }: FatiaDoTextoCertificado,
): string[] {
  const caracteres = Array.from(texto);
  const partes: string[] = [];
  let parte = '';
  let linhas = 1;
  let coluna = 0;
  for (const caractere of caracteres) {
    if (parte.length >= maxCaracteres || linhas >= maxLinhas) {
      partes.push(parte);
      parte = '';
      linhas = 1;
      coluna = 0;
    }
    parte += caractere;
    coluna += larguraDoCaractere(caractere);
    if (caractere === '\n' || coluna >= maxColunas) {
      linhas += 1;
      coluna = 0;
    }
  }
  if (parte) partes.push(parte);
  return partes.length ? partes : ['Sem conteúdo textual registrado.'];
}

/**
 * A última fatia do texto recebe as primeiras opções; grupos adicionais continuam em outro
 * bloco para caber na folha. Rótulos já congelados não são reinterpretados pelo renderer.
 */
export function fatiarMensagemDoCertificado(
  mensagem: MensagemDoCertificado,
): FatiaDaMensagemDoCertificado[] {
  const opcoes =
    mensagem.direcao === 'OUTBOUND' && mensagem.opcoes?.buttons.length
      ? mensagem.opcoes
      : undefined;
  const limite = opcoes
    ? { ...FATIA_DO_BALAO, maxCaracteres: 1000, maxLinhas: 18 }
    : FATIA_DO_BALAO;
  const partes =
    opcoes && mensagem.textoExibido === ''
      ? ['']
      : dividirTextoCertificado(mensagem.textoExibido, limite);
  const fatias: FatiaDaMensagemDoCertificado[] = partes.map((parte, indice) => ({
    parte,
    continuacao: indice > 0,
  }));
  if (!opcoes) return fatias;

  const opcoesPorFatia = 3;
  fatias[fatias.length - 1].opcoes = {
    ...(opcoes.title === undefined ? {} : { title: opcoes.title }),
    buttons: opcoes.buttons.slice(0, opcoesPorFatia),
  };
  for (let indice = opcoesPorFatia; indice < opcoes.buttons.length; indice += opcoesPorFatia) {
    fatias.push({
      parte: '',
      continuacao: true,
      opcoes: { buttons: opcoes.buttons.slice(indice, indice + opcoesPorFatia) },
    });
  }
  return fatias;
}
